import type { PersistedState, PersistenceDriver } from "./transport";

/**
 * Персистентность на IndexedDB. Один драйвер — четыре стора с РАЗНОЙ ценой потери
 * (offline-sync §2.1): реплику снести дёшево, очередь невосстановима.
 *
 * Нативная версия базы отвечает ровно за НАБОР сторов и берётся именно нативной ради
 * побочного эффекта: браузер не даст открыть базу кодом со старой версией. Service worker
 * вполне может отдать старый бандл из кэша, и своё поле такую запись не остановит,
 * а `VersionError` — остановит.
 */
const DB_NAME = "mapcraft";
const DB_VERSION = 1;

const STORES = ["replica", "meta", "queue", "conflicts"] as const;

function open(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = () => {
			const db = request.result;
			for (const name of STORES) {
				if (!db.objectStoreNames.contains(name)) {
					db.createObjectStore(name);
				}
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
		request.onblocked = () => reject(new Error("IndexedDB заблокирована другой вкладкой"));
	});
}

function run<T>(db: IDBDatabase, store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>) {
	return new Promise<T>((resolve, reject) => {
		const tx = db.transaction(store, mode);
		const request = fn(tx.objectStore(store));
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

/**
 * Драйвер IndexedDB. Недоступна (приватный режим, отозванное хранилище) — работаем
 * в памяти: это ТОТ ЖЕ код, другой драйвер, а не отдельный режим UI (offline-sync §12).
 */
export function idbDriver(): PersistenceDriver {
	let ready: Promise<IDBDatabase | null> | null = null;

	const db = () => {
		ready ??= open().catch((e) => {
			console.warn("[store] IndexedDB недоступна, работаем в памяти:", e);
			return null;
		});
		return ready;
	};

	return {
		async load() {
			const database = await db();
			if (!database) {
				return null;
			}
			const [meta, entities, queue, conflicts] = await Promise.all([
				run<PersistedState["meta"] | undefined>(database, "meta", "readonly", (s) => s.get("meta")),
				run<PersistedState["entities"] | undefined>(database, "replica", "readonly", (s) => s.get("entities")),
				run<PersistedState["queue"] | undefined>(database, "queue", "readonly", (s) => s.get("queue")),
				run<PersistedState["conflicts"] | undefined>(database, "conflicts", "readonly", (s) =>
					s.get("conflicts")
				)
			]);
			if (!meta) {
				return null;
			}
			return {
				meta,
				entities: entities ?? { nodes: [], placements: [], links: [], completions: [] },
				queue: queue ?? [],
				conflicts: conflicts ?? []
			};
		},

		async saveMeta(meta) {
			const database = await db();
			if (database) {
				await run(database, "meta", "readwrite", (s) => s.put(meta, "meta"));
			}
		},

		async saveEntities(entities) {
			const database = await db();
			if (database) {
				await run(database, "replica", "readwrite", (s) => s.put(entities, "entities"));
			}
		},

		async saveQueue(queue) {
			const database = await db();
			if (database) {
				await run(database, "queue", "readwrite", (s) => s.put(queue, "queue"));
			}
		},

		async saveConflicts(conflicts) {
			const database = await db();
			if (database) {
				await run(database, "conflicts", "readwrite", (s) => s.put(conflicts, "conflicts"));
			}
		},

		/** Сносится ТОЛЬКО реплика и курсор: очередь и карантин — работа человека. */
		async clearReplica() {
			const database = await db();
			if (!database) {
				return;
			}
			await Promise.all([
				run(database, "replica", "readwrite", (s) => s.clear()),
				run(database, "meta", "readwrite", (s) => s.delete("meta"))
			]);
		},

		async clear() {
			const database = await db();
			if (!database) {
				return;
			}
			await Promise.all(STORES.map((name) => run(database, name, "readwrite", (s) => s.clear())));
		}
	};
}
